/**

 * Resolve where an in-app notification should navigate.

 * Prefer the server-computed `link`; fall back locally for stale rows.

 *

 * Keep fallbacks aligned with backend `inbox-links.js`.

 */



const REPORT_TYPES = new Set([

  'report_generated',

  'report_failed',

  'report_distributed',

  'report_completed',

  'client_report_digest',

  'audit_ready',

]);



const PENDING_WORK_TYPES = new Set([

  'pending_work_assigned',

  'pending_work_blockers',

  'pending_work_unowned',

  'process_run_waiting',

  'process_run_failed',

  'process_runs_stalled',

]);



const WORKFLOW_TYPES = new Set([

  'workflow_run_blocked',

  'workflow_automation_failed',

  'workflow_drifted',

  'workflow_stalled_digest',

]);



const ONBOARDING_TYPES = new Set([

  'onboarding_pack_assigned',

  'onboarding_item_blocked',

  'onboarding_overdue',

  'onboarding_unassigned',

  'onboarding_pack_completed',

  'onboarding_run_completed',

  'onboarding_department_update',

  'onboarding_waiting_digest',

]);



const SUPPORT_TYPES = new Set([

  'support_ticket_created',

  'support_ticket_assigned',

  'support_ticket_reply_client',

  'support_ticket_reply_agent',

  'support_ticket_status_changed',

  'support_ticket_resolved',

  'support_ticket_sla_breach',

  'support_tenant_unlinked',

]);



const APPROVAL_TYPES = new Set([

  'approval_raised',

  'approval_stage_passed',

  'approval_rejected',

  'ad_creatives_client_approved',

  'approval_token_abuse',

]);



const TYPE_DEFAULTS = {

  assignment_made: '/tasks',

  department_queue_ageing: '/workspace',

  tasks_created: '/tasks',

  revision_queue_digest: '/tasks',

  overdue_digest: '/tasks',

  weekly_summary: '/dashboard',

  daily_board_summary: '/dashboard',

  eod_stage_digest: '/dashboard',

  dept_daily_shoutout: '/tasks',

  auto_generate_complete: '/reports',

  new_user: '/settings/people',

  meta_creatives_copy_ready: '/meta-creatives',

  market_intel_run_done: '/market-intel',

  content_handed_to_web_dev: '/tools/content',

  content_live_url_found: '/tools/content',

  budget_pacing_off_track: '/live',

  cpl_anomaly: '/live',
  zero_lead_spend_streak: '/leads/spend-source',

  campaign_day7_review_due: '/campaigns',

  campaign_went_live: '/campaigns',

  client_created: '/clients',

  client_ghl_location_linked: '/clients',

  client_google_ads_linked: '/clients',

  client_meta_ad_linked: '/clients',

  crawl_failed: '/clients/readiness',

  manager_assigned: '/clients',

  cycle_stage_ready: '/clients',

  task_status_changed: '/tasks',

};



function isTaskType(type, item) {

  if (item.taskId || item.subjectType === 'task') return true;

  if (!type) return false;

  return type.startsWith('task_') || type === 'verification_failed';

}



export function notificationLink(item) {

  if (!item) return null;

  // Command Center work-item escalations link INTO Command Center — kept
  // absolute (folding it to a path would open a SpoonFeed route that does not
  // exist). Mirrors backend inbox-links#isCommandCenterLink.
  if (String(item.subjectType || '').startsWith('work_item_') && isAbsoluteUrl(item.link)) {
    return String(item.link).trim();
  }

  const fromApi = normalizePath(item.link);

  if (fromApi) return fromApi;



  const type = item.notificationType || '';

  const taskId = plainId(item.taskId);

  const reportId = plainId(item.reportId);

  const clientId = plainId(item.clientId);

  const subjectType = item.subjectType || null;

  const subjectId = item.subjectId ? String(item.subjectId) : null;



  if (Array.isArray(item.items) && item.items.length === 1 && item.items[0]?.link) {

    return normalizePath(item.items[0].link);

  }



  if (type === 'assignment_made') {

    return '/tasks';

  }



  if (isTaskType(type, item)) {

    const id = taskId || (subjectType === 'task' ? subjectId : null);

    return id ? `/tasks?task=${id}` : '/tasks';

  }



  if (reportId || subjectType === 'report' || REPORT_TYPES.has(type)) {

    const id = reportId || (subjectType === 'report' ? subjectId : null);

    if (id) return `/reports/${id}/review`;

    return '/reports';

  }



  if (subjectType === 'workflow_run' && subjectId) {

    return `/workflows/runs/${subjectId}`;

  }

  if (WORKFLOW_TYPES.has(type)) {

    return '/workflows';

  }



  if (subjectType === 'onboarding_pack' && subjectId) {

    return '/client-onboarding/my-queue';

  }

  if (ONBOARDING_TYPES.has(type)) {

    return '/client-onboarding/my-queue';

  }



  if (subjectType === 'pending_work_gap') {

    const factId = subjectId || item.factId;

    return factId ? `/pending-work?gap=${encodeURIComponent(factId)}` : '/pending-work';

  }

  if (PENDING_WORK_TYPES.has(type)) {

    return clientId ? `/pending-work/${clientId}` : '/pending-work';

  }



  if (subjectType === 'support_ticket' && subjectId) {

    return `/support-center/tickets/${subjectId}`;

  }

  if (subjectType === 'ticket' && subjectId) {

    return `/support-center/tickets/${subjectId}`;

  }

  if (SUPPORT_TYPES.has(type)) {

    return '/support-center';

  }



  if (APPROVAL_TYPES.has(type)) {

    return '/approvals';

  }



  if (clientId) {

    return `/clients/${clientId}`;

  }



  return TYPE_DEFAULTS[type] || null;

}



function plainId(v) {

  if (v == null) return null;

  if (typeof v === 'object' && v._id) return String(v._id);

  return String(v);

}



function normalizePath(raw) {

  if (!raw || typeof raw !== 'string') return null;

  const s = raw.trim();

  if (!s) return null;

  if (s.startsWith('/')) return s;

  try {

    const u = new URL(s);

    return `${u.pathname}${u.search}`;

  } catch {

    return null;

  }

}



export function formatNotificationWhen(iso) {

  if (!iso) return '';

  try {

    const d = new Date(iso);

    return d.toLocaleString(undefined, {

      month: 'short',

      day: 'numeric',

      hour: 'numeric',

      minute: '2-digit',

    });

  } catch {

    return '';

  }

}



const FRIENDLY_TYPE_LABELS = {
  service_plan_changed: 'service plan',
  location_status_changed: 'location status',
  practice_status_changed: 'practice status',
};

export function notificationTypeLabel(type) {

  if (!type) return '';

  return FRIENDLY_TYPE_LABELS[type] || type.replace(/_/g, ' ');

}



function isAbsoluteUrl(v) {
  return /^https?:\/\//i.test(String(v || '').trim());
}

/**

 * Navigate without waiting on mark-read — used by bell + inbox.

 */

export function activateNotification(item, href, { navigate, markRead, onClose }) {

  onClose?.();

  // Another app (Command Center) opens in a new tab; our own routes navigate.
  if (href && isAbsoluteUrl(href)) window.open(href, '_blank', 'noopener,noreferrer');
  else if (href) navigate(href);

  if (item && !item.readAt && markRead) {

    markRead(item._id).catch(() => {});

  }

}


